import React, { Children, useState, useCallback, useEffect } from 'react';
import { isMobile, getPlatformStyles } from '@direct-frontend/platform-code';
import { genericMemo } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';

import { cn } from '../../lib/css/className/className';
import { HStack } from '../Stack';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';

import { AlertLineCounter } from './AlertLineCounter/AlertLineCounter';
import { AlertSwiper } from './AlertSwiper/AlertSwiper';
import { AlertLineProps } from './types';
import { skinMap } from './utils';

getPlatformStyles({
    common: import('./AlertLine.css'),
    mobile: import('./AlertLine@mobile.css'),
    desktop: import('./AlertLine@desktop.css'),
});

const cls = cn('AlertLine');

/**
 * Компонент для отображения полоски с различными типами предупреждений
 */
export const AlertLine = genericMemo(function AlertLine(props: AlertLineProps) {
    const { 'data-testid': dataTestId = 'AlertLine', className, children, logNode } = props;

    const { LogProvider } = useLogger('AlertLine', logNode);

    const sizeSkinClass = getSizeSkin(isMobile() ? 'xs' : 's');

    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const slidesColorList = Children.map(children, child => child.props.color);
    const handlePreviousSlide = useCallback(
        () =>
            setCurrentSlideIndex(prev => {
                if (prev === 0) {
                    return Children.count(children) - 1;
                }

                return prev - 1;
            }),
        [children],
    );
    const handleNextSlide = useCallback(
        () =>
            setCurrentSlideIndex(prev => {
                if (prev === Children.count(children) - 1) {
                    return 0;
                }

                return prev + 1;
            }),
        [children],
    );

    const childrenCount = Children.count(children);
    useEffect(
        () => setCurrentSlideIndex(prev => (childrenCount === 0 ? 0 : Math.min(prev, childrenCount - 1))),
        [childrenCount],
    );

    const handleSwipe = useCallback((index: number) => setCurrentSlideIndex(index), []);
    const currentColor = slidesColorList[currentSlideIndex];
    const colorSkinClass = getColorSkin(skinMap[currentColor ?? 'message'], {});

    return (
        <LogProvider>
            <div className={cls('', [className, sizeSkinClass, colorSkinClass])} data-testid={dataTestId}>
                {isMobile() ? (
                    <AlertSwiper
                        items={Children.toArray(children)}
                        indicatorColor={'current'}
                        onSelectedItemChange={handleSwipe}
                    />
                ) : (
                    <HStack alignItems="flex-start" gap="8">
                        {Children.count(children) > 1 && (
                            <AlertLineCounter
                                currentSlideIndex={currentSlideIndex + 1}
                                slideCount={Children.count(children)}
                                onPrevious={handlePreviousSlide}
                                onNext={handleNextSlide}
                            />
                        )}
                        {Children.map(children, (child, index) => {
                            if (index === currentSlideIndex) {
                                return child;
                            }

                            return undefined;
                        })}
                    </HStack>
                )}
            </div>
        </LogProvider>
    );
});
